import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AuthenticationResult,
  InteractionRequiredAuthError,
  AccountInfo,
  InteractionStatus,
  EventType,
  EventMessage,
} from '@azure/msal-browser';
import { GraphUserService } from './graph-user.service';
import { TokenService } from './token.service';
import { RoleService } from './role.service';
import { environment } from './../../../../environments/environment';
import { switchMap, catchError, tap, filter, first } from 'rxjs/operators';
import { throwError, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _msalService = inject(MsalService);
  private readonly _msalBroadcastService = inject(MsalBroadcastService);
  private readonly _graphUserService = inject(GraphUserService);
  private readonly _tokenService = inject(TokenService);
  private readonly _roleService = inject(RoleService);

  public authReady: WritableSignal<boolean> = signal(false);
  private readonly _initialAuthResolutionAttempted = signal(false);

  constructor() {
    this._msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        first()
      )
      .subscribe(() => {
        this.attemptInitialAuthResolution();
      });

    this._msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        )
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        if (payload && payload.account) {
          this.setActiveAccount(payload.account);
          if (
            !this.authReady() &&
            payload.scopes.some(
              (s) =>
                s.toLowerCase() ===
                environment.msalConfig.apiScope.toLowerCase()
            )
          ) {
            this.setToken(payload.accessToken);
          } else if (
            !this.authReady() &&
            result.eventType === EventType.LOGIN_SUCCESS
          ) {
            this.acquireTokenForApp(payload.account).subscribe({
              next: (apiTokenResponse) =>
                this.setToken(apiTokenResponse.accessToken),
              error: (err) => {
                console.error(
                  '[AuthService] Error acquiring API token after generic LOGIN_SUCCESS in msalSubject$:',
                  err
                );
                this.clearToken();
              },
            });
          }
        }
      });

    this._msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg) =>
            msg.eventType === EventType.LOGOUT_SUCCESS ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe(() => {
        console.log(
          '[AuthService] MSAL Event: LOGOUT_SUCCESS or ACCOUNT_REMOVED. Clearing local token and roles.'
        );
        this.clearToken();
      });
  }

  private attemptInitialAuthResolution(): void {
    if (this._initialAuthResolutionAttempted()) {
      return;
    }
    this._initialAuthResolutionAttempted.set(true);
    console.log('[AuthService] attemptInitialAuthResolution: Executing...');

    this.setActiveAccount();
    const msalAccount = this._msalService.instance.getActiveAccount();

    if (msalAccount) {
      console.log(
        '[AuthService] attemptInitialAuthResolution: Active MSAL account found:',
        msalAccount.username
      );
      this.acquireTokenForApp(msalAccount).subscribe({
        next: (apiTokenResponse) => {
          console.log(
            '[AuthService] attemptInitialAuthResolution: API token acquired successfully.'
          );
          this.setToken(apiTokenResponse.accessToken);
        },
        error: (err) => {
          console.error(
            '[AuthService] attemptInitialAuthResolution: Failed to acquire API token.',
            err
          );
          this.clearToken();
        },
      });
    } else {
      console.log(
        '[AuthService] attemptInitialAuthResolution: No active MSAL account found. Setting authReady to false.'
      );
      this.authReady.set(false);
    }
  }

  public setActiveAccount(account?: AccountInfo | null): void {
    let activeAccount = account;
    if (!activeAccount) {
      const allAccounts = this._msalService.instance.getAllAccounts();
      if (allAccounts.length > 0) {
        activeAccount = allAccounts[0];
      }
    }
    if (activeAccount) {
      if (
        this._msalService.instance.getActiveAccount()?.homeAccountId !==
        activeAccount.homeAccountId
      ) {
        this._msalService.instance.setActiveAccount(activeAccount);
        console.log(
          '[AuthService] Active MSAL account set to:',
          activeAccount.username
        );
      }
    }
  }

  private acquireTokenForApp(
    account?: AccountInfo | null
  ): Observable<AuthenticationResult> {
    console.log(
      '[AuthService] acquireTokenForApp: Attempting to acquire token for API scope:',
      environment.msalConfig.apiScope
    );
    const currentAccount =
      account || this._msalService.instance.getActiveAccount();
    if (!currentAccount) {
      console.error(
        '[AuthService] acquireTokenForApp: No active account to acquire token for.'
      );
      return throwError(
        () => new Error('No active account for acquireTokenSilent.')
      );
    }
    return this._msalService
      .acquireTokenSilent({
        scopes: [environment.msalConfig.apiScope],
        account: currentAccount,
      })
      .pipe(
        catchError((error) => {
          console.warn(
            '[AuthService] acquireTokenForApp: Silent token acquisition failed.',
            error
          );
          if (error instanceof InteractionRequiredAuthError) {
            console.log(
              '[AuthService] acquireTokenForApp: Interaction required. Initiating acquireTokenRedirect.'
            );
            this._msalService.acquireTokenRedirect({
              scopes: [environment.msalConfig.apiScope],
            });

            return new Observable<never>(() => {
              console.log(
                '[AuthService] Redirect initiated, page will navigate away'
              );
            });
          }
          return throwError(() => error);
        })
      );
  }

  public handleRedirect() {
    console.log(
      '[AuthService] handleRedirect: Called. Subscribing to handleRedirectObservable.'
    );
    this._msalService
      .handleRedirectObservable()
      .pipe(
        filter(
          (
            msalResponse: AuthenticationResult | null
          ): msalResponse is AuthenticationResult =>
            msalResponse !== null && msalResponse.account !== null
        ),
        first()
      )
      .subscribe({
        next: (msalResponse) => {
          console.log(
            '[AuthService] handleRedirect: Valid MSAL redirect response received. Account:',
            msalResponse.account!.username
          );
          this.authReady.set(false); // Inicia el proceso, resetea authReady
          this.setActiveAccount(msalResponse.account);
          this.acquireTokenForApp(msalResponse.account).subscribe({
            next: (apiTokenResponse) => {
              console.log(
                '[AuthService] handleRedirect: API token acquired successfully after redirect.'
              );
              this.setToken(apiTokenResponse.accessToken);
            },
            error: (err) => {
              console.error(
                '[AuthService] handleRedirect: Error acquiring API token after redirect.',
                err
              );
              this.clearToken();
            },
          });
        },
        error: (err) => {
          console.error(
            '[AuthService] handleRedirect: Error from handleRedirectObservable itself.',
            err
          );
          this.clearToken();
        },
        complete: () => {
          console.log(
            '[AuthService] handleRedirectObservable completed. If not a redirect login, initial check will handle auth state.'
          );
        },
      });
  }

  public getToken(): string | null {
    return this._tokenService.getToken();
  }

  setToken(token: string) {
    console.log(
      '[AuthService] setToken: Setting app token, initializing roles.'
    );
    this._tokenService.setToken(token);
    this._roleService.initRoles();

    if (this._roleService.userRoles().length > 0) {
      this.authReady.set(true);
      console.log(
        '[AuthService] setToken: Roles found. authReady signal set to true. Roles:',
        this._roleService.userRoles()
      );
    } else {
      console.warn(
        '[AuthService] setToken: Token processed, but NO roles found after initRoles. This token might be missing the "roles" claim or claim is empty. authReady forced to false.'
      );
      this.authReady.set(true);
    }
  }

  clearToken() {
    console.log(
      '[AuthService] clearToken: Clearing app token, roles, and setting authReady to false.'
    );
    this._tokenService.clearToken();
    this._roleService.clearRoles();
    this.authReady.set(false);
  }

  public isLoggedIn(): boolean {
    const msalAccountActive =
      this._msalService.instance.getActiveAccount() !== null;
    return msalAccountActive && this.authReady();
  }

  public loginRedirect() {
    this.authReady.set(false);
    this._msalService.loginRedirect();
  }

  public logout() {
    console.log('[AuthService] logout: Initiating logout.');
    const activeAccount = this._msalService.instance.getActiveAccount();
    this.clearToken();
    this._msalService.logoutRedirect({ account: activeAccount });
  }

  public checkAndRenewToken(scopes: string[]): void {
    console.log('[AuthService] checkAndRenewToken: Called. Scopes:', scopes);
    const account = this._msalService.instance.getActiveAccount();
    if (!account) {
      console.warn(
        '[AuthService] checkAndRenewToken: No active MSAL account. Cannot renew.'
      );
      if (this.authReady()) {
        this.clearToken();
      }
      return;
    }

    console.log(
      '[AuthService] checkAndRenewToken: Attempting to acquire/renew token for API.'
    );
    this.acquireTokenForApp(account).subscribe({
      next: (response) => {
        console.log(
          '[AuthService] checkAndRenewToken: Token for app API ensured/renewed successfully.'
        );
        this.setToken(response.accessToken);
      },
      error: (error) => {
        console.error(
          '[AuthService] checkAndRenewToken: Failed to ensure/renew token for app API.',
          error
        );
        if (this.authReady()) {
          this.clearToken();
        }
      },
    });
  }

  public getUserProfile() {
    this._graphUserService.getUserProfile().subscribe({
      next: (user) => {
        this._graphUserService.graphUser.set(user);
      },
      error: (error) => {
        console.error('Error fetching user profile:', error);
      },
    });
  }
}
